import React, { useState } from 'react';
import { Send, Loader2 } from 'lucide-react';
import { Button } from '../ui/button';
import '../../ContactForm.css';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const INITIAL_FORM = {
  name: '',
  email: '',
  phone: '',
  organization: '',
  interest: '',
  ageGroup: '',
  message: '',
};

const JamboreeInterestForm = () => {
  const [formData, setFormData] = useState(INITIAL_FORM);
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    // Compose a structured message for the admin inbox
    const composedMessage = [
      `Interest: ${formData.interest || 'Not specified'}`,
      `Organization / Team: ${formData.organization || 'N/A'}`,
      `Age Group / Division: ${formData.ageGroup || 'N/A'}`,
      '',
      'Message:',
      formData.message || '(none)',
    ].join('\n');

    const payload = {
      name: formData.name,
      email: formData.email,
      phone: formData.phone,
      subject: `Jamboree Interest — ${formData.interest || 'General'}`,
      message: composedMessage,
    };

    try {
      const response = await fetch(`${API_URL}/api/contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      const data = await response.json();
      if (data.success) {
        setSubmitted(true);
        setFormData(INITIAL_FORM);
      } else {
        setError(data.message || 'Failed to send. Please try again.');
      }
    } catch {
      setError('Network error. Please check your connection and try again.');
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div className="contact-form-success" data-testid="jamboree-form-success">
        <div className="success-checkmark">✓</div>
        <h3>Interest Received!</h3>
        <p>Thanks for reaching out. A Panthers coach will follow up with you shortly.</p>
        <Button
          className="btn-primary"
          data-testid="jamboree-form-reset-btn"
          onClick={() => setSubmitted(false)}
        >
          Submit Another
        </Button>
      </div>
    );
  }

  return (
    <form
      onSubmit={handleSubmit}
      className="contact-form"
      data-testid="jamboree-interest-form"
    >
      {error && <div className="form-error" data-testid="jamboree-form-error">{error}</div>}

      <div className="form-row">
        <div className="form-group">
          <label htmlFor="jam-name">Your Name *</label>
          <input
            type="text"
            id="jam-name"
            name="name"
            value={formData.name}
            onChange={handleChange}
            required
            placeholder="Coach, parent, or athlete"
            disabled={loading}
            data-testid="jamboree-form-name"
          />
        </div>
        <div className="form-group">
          <label htmlFor="jam-email">Email *</label>
          <input
            type="email"
            id="jam-email"
            name="email"
            value={formData.email}
            onChange={handleChange}
            required
            placeholder="your.email@example.com"
            disabled={loading}
            data-testid="jamboree-form-email"
          />
        </div>
      </div>

      <div className="form-row">
        <div className="form-group">
          <label htmlFor="jam-phone">Phone</label>
          <input
            type="tel"
            id="jam-phone"
            name="phone"
            value={formData.phone}
            onChange={handleChange}
            placeholder="(XXX) XXX-XXXX"
            disabled={loading}
            data-testid="jamboree-form-phone"
          />
        </div>
        <div className="form-group">
          <label htmlFor="jam-interest">Interest *</label>
          <select
            id="jam-interest"
            name="interest"
            value={formData.interest}
            onChange={handleChange}
            required
            disabled={loading}
            data-testid="jamboree-form-interest"
          >
            <option value="">Select one...</option>
            <option value="Football Team Registration">Football Team Registration</option>
            <option value="Flag Football Team Registration">Flag Football Team Registration</option>
            <option value="Girls Flag Football (Ages 12-14)">Girls Flag Football (Ages 12-14)</option>
            <option value="Cheer Squad Registration">Cheer Squad Registration</option>
            <option value="Spectator / General Info">Spectator / General Info</option>
            <option value="Vendor / Sponsorship">Vendor / Sponsorship</option>
          </select>
        </div>
      </div>

      <div className="form-row">
        <div className="form-group">
          <label htmlFor="jam-org">Organization / Team Name</label>
          <input
            type="text"
            id="jam-org"
            name="organization"
            value={formData.organization}
            onChange={handleChange}
            placeholder="e.g. GA Gladiators"
            disabled={loading}
            data-testid="jamboree-form-organization"
          />
        </div>
        <div className="form-group">
          <label htmlFor="jam-age">Age Group / Division</label>
          <input
            type="text"
            id="jam-age"
            name="ageGroup"
            value={formData.ageGroup}
            onChange={handleChange}
            placeholder="e.g. 8U, 10U, Flag 5–6"
            disabled={loading}
            data-testid="jamboree-form-age"
          />
        </div>
      </div>

      <div className="form-group">
        <label htmlFor="jam-message">Additional Notes</label>
        <textarea
          id="jam-message"
          name="message"
          value={formData.message}
          onChange={handleChange}
          placeholder="Anything we should know? (optional)"
          rows={4}
          disabled={loading}
          data-testid="jamboree-form-message"
        />
      </div>

      <Button
        type="submit"
        className="btn-primary submit-btn"
        disabled={loading}
        data-testid="jamboree-form-submit"
      >
        {loading ? (
          <>
            <Loader2 className="animate-spin" size={18} />
            Sending...
          </>
        ) : (
          <>
            <Send size={18} />
            Submit Interest
          </>
        )}
      </Button>
    </form>
  );
};

export default JamboreeInterestForm;
